<header
  role="banner"
  class="fixed flex w-full top-0 h-[70px] lg:h-[92px] z-[55] transition-transform duration-300"
  [ngClass]="{
    'translate-y-0': isHeaderVisible || isMobileMenuOpen,
    '-translate-y-[70px] lg:-translate-y-[112px]': !isHeaderVisible &&
      !isMobileMenuOpen
  }"
>
  <div class="mt-4 lg:mt-10 w-full">
    <nav
      class="w-full lg:w-fit max-w-[calc(100%-24px)] mx-3 lg:mx-auto h-full border border-gray-200 dark:border-gray-700 rounded-full backdrop-blur-xl bg-white/60 dark:bg-gray-800/60 px-4 lg:px-6 lg:h-[72px]"
      role="navigation"
      aria-label="Main navigation"
    >
      <div class="flex justify-between items-center h-full">
        <!-- Logo -->
        <a
          [routerLink]="authService.isAuthenticated() ? '/dashboard' : '/'"
          class="flex items-center gap-2"
          [attr.aria-label]="authService.isAuthenticated() ? 'DesignJobs Dashboard' : 'DesignJobs Home'"
        >
          <!-- Light Mode Logo -->
          <img
            src="/assets/svgs/logo.svg"
            class="dark:hidden max-w-[120px] lg:max-w-[140px] mt-[-2px] md:mt-[-4px]"
            alt="Logo"
          />

          <!-- Dark Mode Logo -->
          <img
            src="/assets/svgs/logo-white.svg"
            class="hidden dark:block max-w-[120px] lg:max-w-[150px] mt-[-2px] md:mt-[-4px]"
            alt="Logo White"
          />
        </a>

        <!-- Desktop Navigation - Centered -->
        <div
          class="hidden lg:flex items-center justify-center px-4 lg:pl-[80px] lg:pr-[60px] h-full shrink-0"
        >
          <div
            class="nav-menu flex items-center gap-0.5 h-full relative"
            [ngClass]="{ 'pt-1': authService.isAuthenticated() }"
            (mouseleave)="onNavMenuLeave()"
          >
            <!-- Navigation Background -->
            <div
              class="nav-background absolute top-1/2 transform -translate-y-1/2 bg-[#EBEDF0] dark:bg-gray-700 rounded-full h-10 transition-all duration-300 ease-out pointer-events-none"
              [class.opacity-0]="!navBackgroundVisible"
              [class.opacity-100]="navBackgroundVisible"
              [style.left]="navBackgroundStyle.left"
              [style.width]="navBackgroundStyle.width"
            ></div>
            <!-- Show these menu items only when logged in -->
            <ng-container *ngIf="authService.isAuthenticated()">
              <a
                routerLink="/dashboard"
                routerLinkActive="nav-active"
                class="nav-link flex flex-col items-center justify-center h-full px-2.5 transition-colors"
              >
                <hugeicons-icon
                  [icon]="icons.home"
                  [size]="20"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="block mb-0.5"
                />
                <span class="text-[13px]" data-text="Dashboard">Dashboard</span>
              </a>

              <a
                routerLink="/jobs"
                routerLinkActive="nav-active"
                class="nav-link flex flex-col items-center justify-center h-full px-2.5 transition-colors"
              >
                <hugeicons-icon
                  [icon]="icons.briefcase"
                  [size]="20"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="block mb-0.5"
                />
                <span class="text-[13px]" data-text="Jobs">Jobs</span>
              </a>

              <a
                routerLink="/job-matches"
                routerLinkActive="nav-active"
                class="nav-link flex flex-col items-center justify-center h-full px-2.5 transition-colors relative"
              >
                <hugeicons-icon
                  [icon]="icons.clipboard"
                  [size]="20"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="block mb-0.5"
                />
                <span class="text-[13px]" data-text="Matches">Matches</span>
                <span
                  *ngIf="jobMatchesCount > 0"
                  [ngClass]="{
                    'absolute -top-1 -right-1 flex items-center justify-center bg-brand-gradient text-white text-[10px] rounded-full': true,
                    'h-[18px] w-[18px]': jobMatchesCount <= 99,
                    'h-[18px] w-[28px]': jobMatchesCount > 99
                  }"
                >
                  {{ jobMatchesCount > 99 ? "99+" : jobMatchesCount }}
                </span>
              </a>

              <!-- Profile Link for Desktop -->
              <a
                routerLink="/profile"
                routerLinkActive="nav-active"
                [routerLinkActiveOptions]="{ exact: false }"
                class="nav-link flex flex-col items-center justify-center h-full px-2.5 transition-colors"
              >
                <hugeicons-icon
                  [icon]="icons.user"
                  [size]="20"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="block mb-0.5"
                />
                <span class="text-[13px]" data-text="Profile">Profile</span>
              </a>

              <!-- Job Alerts Link for Desktop -->
              <a
                routerLink="/job-alerts"
                routerLinkActive="nav-active"
                class="nav-link flex flex-col items-center justify-center h-full px-2.5 transition-colors"
              >
                <hugeicons-icon
                  [icon]="icons.notification"
                  [size]="20"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="block mb-0.5"
                />
                <span class="text-[13px]" data-text="Alerts">Alerts</span>
              </a>
            </ng-container>

            <!-- Show these menu items when logged out -->
            <ng-container *ngIf="!authService.isAuthenticated()">
              <!-- Find Work -->
              <div class="nav-item" (mouseenter)="onNavItemHover($event)">
                <button class="nav-trigger" (mouseenter)="openMenu('findWork')">
                  Find work
                  <hugeicons-icon
                    [icon]="icons.arrowDown"
                    [size]="16"
                    color="currentColor"
                    [strokeWidth]="2"
                    class="transition-transform duration-200"
                    [class.rotate-180]="showFindWorkMenu"
                  />
                </button>

                <div
                  class="nav-dropdown"
                  [class.active]="showFindWorkMenu"
                  (mouseleave)="closeAllMenus()"
                  style="z-index: 2"
                >
                  <div class="flex p-3 bg-white dark:bg-gray-900">
                    <!-- Left Column - Navigation Items -->
                    <div class="w-[400px] space-y-3 pr-4">
                      <a
                        routerLink="/jobs"
                        class="flex items-center group hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 transition-colors"
                      >
                        <div
                          class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-blue-100 rounded-md flex items-center justify-center mr-3"
                        >
                          <span class="text-lg">💼</span>
                        </div>
                        <div class="flex-1">
                          <h3
                            class="text-base font-bold text-gray-900 dark:text-white"
                          >
                            Browse jobs
                          </h3>
                          <p class="text-sm text-gray-600 dark:text-gray-400">
                            Find your next design role
                          </p>
                        </div>
                      </a>

                      <a
                        routerLink="/ai-job-matcher"
                        class="flex items-center group hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 transition-colors"
                      >
                        <div
                          class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-green-100 rounded-md flex items-center justify-center mr-3"
                        >
                          <span class="text-lg">💡</span>
                        </div>
                        <div class="flex-1">
                          <h3
                            class="text-base font-bold text-gray-900 dark:text-white"
                          >
                            Job matcher
                          </h3>
                          <p class="text-sm text-gray-600 dark:text-gray-400">
                            Get matched with roles that fit
                          </p>
                        </div>
                      </a>

                      <a
                        routerLink="/blog"
                        class="flex items-center group hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 transition-colors"
                      >
                        <div
                          class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-yellow-100 rounded-md flex items-center justify-center mr-3"
                        >
                          <span class="text-lg">📖</span>
                        </div>
                        <div class="flex-1">
                          <h3
                            class="text-base font-bold text-gray-900 dark:text-white"
                          >
                            Blog
                          </h3>
                          <p class="text-sm text-gray-600 dark:text-gray-400">
                            Tools and guides for your design career
                          </p>
                        </div>
                      </a>

                      <a
                        routerLink="/companies-hiring-this-week"
                        class="flex items-center group hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 transition-colors"
                      >
                        <div
                          class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-pink-100 rounded-md flex items-center justify-center mr-3"
                        >
                          <span class="text-lg">🏢</span>
                        </div>
                        <div class="flex-1">
                          <h3
                            class="text-base font-bold text-gray-900 dark:text-white"
                          >
                            Companies hiring
                          </h3>
                          <p class="text-sm text-gray-600 dark:text-gray-400">
                            See who's hiring this week
                          </p>
                        </div>
                      </a>
                    </div>

                    <!-- Right Column - Create Profile Section -->
                    <div class="self-stretch flex min-w-0">
                      <div
                        class="create-profile-card-link max-h-[310px] flex flex-col flex-1 min-h-full rounded-lg border border-gray-200 dark:border-gray-600 bg-gradient-to-b from-white to-[#FFE2F9] dark:from-gray-800 dark:to-gray-700 p-4 overflow-hidden relative hover:border-gray-400 dark:hover:border-gray-400 transition-colors cursor-pointer"
                        (click)="navigateToProfile()"
                      >
                        <div class="relative z-10 flex flex-1 flex-col min-h-0">
                          <h3
                            class="text-base font-bold text-gray-900 dark:text-white mb-1 flex items-center"
                          >
                            Create your profile
                          </h3>
                          <p
                            class="text-sm text-gray-600 dark:text-gray-400 mb-1.5"
                          >
                            Build your profile and let opportunities find you
                          </p>
                          <a
                            href="https://designjobs.careers/carlos"
                            target="_blank"
                            rel="noopener noreferrer"
                            (click)="$event.stopPropagation()"
                            class="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-500 rounded block mb-2"
                          >
                            View example
                          </a>
                          <!-- Profile card preview (matches homepage Custom profile card) -->
                          <div
                            class="profile-card-preview mt-[16px] flex flex-col items-center text-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pt-9 pb-5 px-4 min-h-[200px]"
                          >
                            <div
                              class="profile-preview-avatar-wrap relative -mt-[52px] mb-1.5"
                            >
                              <img
                                src="assets/images/homepage/avatar.png"
                                alt="Ben Smith"
                                class="w-16 h-16 rounded-full object-cover shadow-sm"
                              />
                              <img
                                src="assets/images/homepage/verified.svg"
                                alt=""
                                aria-hidden="true"
                                class="profile-preview-verified absolute top-0.5 -right-12 w-10 h-10 object-contain"
                              />
                            </div>
                            <div
                              class="profile-preview-name text-sm font-bold text-gray-900 dark:text-white"
                            >
                              Ben Smith
                            </div>
                            <div
                              class="profile-preview-loc text-[11px] text-gray-500 dark:text-gray-400 mb-1"
                            >
                              🇺🇸 New York, USA
                            </div>
                            <p
                              class="profile-preview-bio text-xs text-gray-600 dark:text-gray-300 leading-snug mb-2 max-w-[180px]"
                            >
                              Product Designer with 10+ years of experience
                            </p>
                            <span
                              class="profile-preview-badge inline-flex items-center gap-1.5 text-[10px] font-semibold text-green-800 dark:text-green-300 bg-green-100 dark:bg-green-900/40 px-2.5 py-1 rounded-full"
                            >
                              <span
                                class="w-1.5 h-1.5 rounded-full bg-green-600 shrink-0"
                                aria-hidden="true"
                              ></span>
                              Available for work
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Find Talent -->
              <div class="nav-item" (mouseenter)="onNavItemHover($event)">
                <button
                  class="nav-trigger"
                  (mouseenter)="openMenu('findTalent')"
                >
                  Find talent
                  <hugeicons-icon
                    [icon]="icons.arrowDown"
                    [size]="16"
                    color="currentColor"
                    [strokeWidth]="2"
                    class="transition-transform duration-200"
                    [class.rotate-180]="showFindTalentMenu"
                  />
                </button>

                <div
                  class="nav-dropdown w-[340px]"
                  [class.active]="showFindTalentMenu"
                  (mouseleave)="closeAllMenus()"
                >
                  <div class="p-3 bg-white dark:bg-gray-900 space-y-3">
                    <a
                      routerLink="/hire-designers"
                      class="flex items-center group hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 transition-colors"
                    >
                      <div
                        class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-purple-100 rounded-md flex items-center justify-center mr-3"
                      >
                        <span class="text-lg">📊</span>
                      </div>
                      <div class="flex-1">
                        <h3
                          class="text-base font-bold text-gray-900 dark:text-white"
                        >
                          Hire designers
                        </h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400">
                          Why post on DesignJobs &amp; how it works
                        </p>
                      </div>
                    </a>

                    <a
                      routerLink="/post-job"
                      class="flex items-center group hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 transition-colors"
                    >
                      <div
                        class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-orange-100 rounded-md flex items-center justify-center mr-3"
                      >
                        <span class="text-lg">✏️</span>
                      </div>
                      <div class="flex-1">
                        <h3
                          class="text-base font-bold text-gray-900 dark:text-white"
                        >
                          Post a job
                        </h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400">
                          Create a job listing
                        </p>
                      </div>
                    </a>

                    <div class="flex items-center rounded-lg p-2 relative">
                      <!-- Coming Soon Badge -->
                      <div class="absolute top-1 right-2 z-20">
                        <span
                          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm"
                        >
                          Coming Soon
                        </span>
                      </div>

                      <div
                        class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-teal-100 rounded-md flex items-center justify-center mr-3"
                      >
                        <span class="text-lg">👥</span>
                      </div>
                      <div class="flex-1">
                        <h3
                          class="text-base font-bold text-gray-900 dark:text-white"
                        >
                          Find talent
                        </h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400">
                          Access our curated pool of design professionals
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Blog -->
              <div class="nav-item" (mouseenter)="onNavItemHover($event)">
                <button class="nav-trigger" (mouseenter)="openMenu('blog')">
                  Blog
                  <hugeicons-icon
                    [icon]="icons.arrowDown"
                    [size]="16"
                    color="currentColor"
                    [strokeWidth]="2"
                    class="transition-transform duration-200"
                    [class.rotate-180]="showBlogMenu"
                  />
                </button>

                <div
                  class="nav-dropdown w-[400px]"
                  [class.active]="showBlogMenu"
                  (mouseleave)="closeAllMenus()"
                >
                  <div class="nav-dropdown-grid">
                    <a
                      routerLink="/blog"
                      (click)="closeAllMenus()"
                      class="nav-dropdown-item"
                    >
                      <div class="nav-dropdown-item-title">All articles</div>
                      <div class="nav-dropdown-item-description">
                        View all blog posts
                      </div>
                    </a>
                    <a
                      routerLink="/blog"
                      [queryParams]="{ topic: 'interviews' }"
                      (click)="closeAllMenus()"
                      class="nav-dropdown-item"
                    >
                      <div class="nav-dropdown-item-title">Interviews</div>
                      <div class="nav-dropdown-item-description">
                        Get behind the screens of top designers' careers
                      </div>
                    </a>
                    <a
                      routerLink="/blog"
                      [queryParams]="{ topic: 'portfolio' }"
                      (click)="closeAllMenus()"
                      class="nav-dropdown-item"
                    >
                      <div class="nav-dropdown-item-title">
                        Portfolio building
                      </div>
                      <div class="nav-dropdown-item-description">
                        Create a standout portfolio
                      </div>
                    </a>
                    <a
                      routerLink="/blog"
                      [queryParams]="{ topic: 'advice' }"
                      (click)="closeAllMenus()"
                      class="nav-dropdown-item"
                    >
                      <div class="nav-dropdown-item-title">Advice</div>
                      <div class="nav-dropdown-item-description">
                        General career advice
                      </div>
                    </a>
                    <a
                      routerLink="/blog"
                      [queryParams]="{ topic: 'career-growth' }"
                      (click)="closeAllMenus()"
                      class="nav-dropdown-item"
                    >
                      <div class="nav-dropdown-item-title">Career growth</div>
                      <div class="nav-dropdown-item-description">
                        Level up your design career
                      </div>
                    </a>
                    <a
                      routerLink="/blog"
                      [queryParams]="{ topic: 'tools' }"
                      (click)="closeAllMenus()"
                      class="nav-dropdown-item"
                    >
                      <div class="nav-dropdown-item-title">Design tools</div>
                      <div class="nav-dropdown-item-description">
                        Master essential tools
                      </div>
                    </a>
                    <a
                      routerLink="/portfolios"
                      (click)="closeAllMenus()"
                      class="nav-dropdown-item"
                    >
                      <div class="nav-dropdown-item-title">
                        Top Portfolios 2026
                  </div>
                      <div class="nav-dropdown-item-description">
                        Explore exceptional portfolio examples
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- Right Side Actions -->
        <div class="flex items-center gap-1 lg:gap-4">
          <!-- When not authenticated, show Login and Sign up buttons -->
          <ng-container *ngIf="!authService.isAuthenticated()">
            <a
              href="/"
              (click)="login($event)"
              class="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors hidden lg:block cursor-pointer text-sm"
            >
              Login
            </a>
            <a
              href="/"
              (click)="register($event)"
              class="btn-primary font-semibold inline-flex shrink-0 items-center justify-center whitespace-nowrap cursor-pointer max-lg:!min-h-8 max-lg:!h-8 max-lg:!py-2 max-lg:!px-4 max-lg:!text-sm lg:!min-h-[2.8375rem] lg:!h-[2.8375rem] lg:!py-3"
            >
              Sign up
            </a>
          </ng-container>

          <div
            class="flex items-center gap-2 lg:gap-4"
            *ngIf="authService.isAuthenticated()"
          >
            <!-- Post a job (desktop only; mobile has it in the menu) -->
            <a
              routerLink="/post-job"
              class="btn-primary btn-sm font-semibold hidden lg:inline-flex cursor-pointer"
            >
              Post a job
            </a>
            <!-- User Avatar with Dropdown (hidden on mobile; actions are in side menu) -->
            <div
              *ngIf="authService.currentUser$ | async as user"
              class="relative hidden lg:block"
            >
              <button
                (click)="toggleUserMenu($event)"
                class="user-menu-trigger flex items-center gap-1 focus:outline-none"
                aria-label="User menu"
              >
                <div
                  class="w-9 h-9 rounded-full bg-gray-300 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center overflow-hidden relative avatar-shimmer hover:opacity-80 transition-opacity"
                >
                  <img
                    *ngIf="(authService.userProfile$ | async)?.profile_picture_url as avatarUrl"
                    [src]="getAvatarUrl(avatarUrl)"
                    alt="User avatar"
                    class="w-full h-full object-cover"
                    (error)="handleImageError($event)"
                  />
                  <span
                    *ngIf="!(authService.userProfile$ | async)?.profile_picture_url"
                    class="font-medium text-sm"
                  >
                    {{ user?.email?.charAt(0)?.toUpperCase() || "U" }}
                  </span>
                </div>
                <hugeicons-icon
                  [icon]="icons.arrowDown"
                  [size]="16"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="transition-transform duration-200 md:block"
                  [class.rotate-180]="showUserMenu"
                />
              </button>

              <!-- User Dropdown Menu -->
              <div
                class="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden z-50 transition-opacity duration-200"
                [class.opacity-0]="!showUserMenu"
                [class.opacity-100]="showUserMenu"
                [class.invisible]="!showUserMenu"
                [class.visible]="showUserMenu"
                (mouseleave)="showUserMenu = false"
              >
                <!-- User info section (only when first or last name is set) -->
                <ng-container
                  *ngIf="{ user: authService.currentUser$ | async, profile: authService.userProfile$ | async } as data"
                >
                  <ng-container
                    *ngIf="data.profile?.first_name || data.profile?.last_name"
                  >
                    <div
                      class="px-4 py-3 bg-gray-50 dark:bg-gray-700/80 border-b border-gray-200 dark:border-gray-600"
                    >
                      <div
                        class="font-semibold text-gray-900 dark:text-white truncate text-sm"
                      >
                        {{
                          data.profile?.display_name ||
                            (data.profile?.first_name || "") +
                              (data.profile?.last_name
                                ? " " + data.profile.last_name
                                : "")
                        }}
                      </div>
                      <div
                        class="text-xs text-gray-600 dark:text-gray-400 truncate mt-0.5"
                      >
                        {{ data.user?.email }}
                      </div>
                    </div>
                  </ng-container>
                </ng-container>
                <div class="py-1.5">
                  <!-- Theme Toggle -->
                  <button
                    (click)="themeService.toggleTheme()"
                    class="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <hugeicons-icon
                      [icon]="(themeService.isDarkTheme$ | async) ? icons.sun : icons.moon"
                      [size]="20"
                      color="currentColor"
                      [strokeWidth]="2"
                      class="mr-1.5 block"
                    />
                    Switch to
                    {{ (themeService.isDarkTheme$ | async) ? "light" : "dark" }}
                    mode
                  </button>

                  <!-- Help & Support -->
                  <a
                    routerLink="/contact"
                    class="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <hugeicons-icon
                      [icon]="icons.help"
                      [size]="20"
                      color="currentColor"
                      [strokeWidth]="2"
                      class="mr-1.5 block"
                    />
                    Help & Support
                  </a>

                  <!-- Logout -->
                  <button
                    (click)="logout()"
                    class="flex items-center w-full px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <hugeicons-icon
                      [icon]="icons.logout"
                      [size]="20"
                      color="currentColor"
                      [strokeWidth]="2"
                      class="mr-1.5 block"
                    />
                    Log out
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile Menu Button -->
          <button
            class="lg:hidden flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none"
            aria-expanded="false"
            (click)="toggleMobileMenu()"
            aria-label="Toggle navigation menu"
          >
            <span class="sr-only">Open main menu</span>
            <hugeicons-icon
              *ngIf="!isMobileMenuOpen"
              [icon]="icons.menu"
              [size]="24"
              color="currentColor"
              [strokeWidth]="2"
              class="block"
              aria-hidden="true"
            />
            <hugeicons-icon
              *ngIf="isMobileMenuOpen"
              [icon]="icons.cancel"
              [size]="24"
              color="currentColor"
              [strokeWidth]="2"
              class="block"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <!-- Progress Bar Container -->
      <div
        *ngIf="progressBarVisible$ | async"
        class="hidden w-full bg-gray-200 dark:bg-gray-700 h-1 absolute bottom-0 left-0"
      >
        <div
          class="bg-teal-500 h-1 transition-all duration-300 ease-in-out"
          [style.width.%]="progressPercentage$ | async"
        ></div>
      </div>
    </nav>
  </div>
</header>

<!-- Spacer to offset the fixed header (only when not using side nav) -->
<div class="h-[70px] lg:h-[92px]"></div>

<!-- Mobile Menu Backdrop -->
<app-backdrop 
  [visible]="isMobileMenuOpen" 
  [zIndex]="60" 
  (backdropClick)="closeMobileMenu()"
  class="lg:hidden"
></app-backdrop>

<!-- Mobile Menu - Full width. data-lenis-prevent allows native scroll when menu is long. -->
<div
  class="lg:hidden fixed top-0 left-0 h-full bg-white dark:bg-gray-900 z-[70] w-full transform translate-x-full transition-transform duration-300 ease-in-out overflow-y-auto"
  data-lenis-prevent
  [class.translate-x-0]="isMobileMenuOpen"
  [class.translate-x-full]="!isMobileMenuOpen"
  [class.pointer-events-none]="!isMobileMenuOpen"
>
  <div class="px-4 pb-4">
    <!-- Mobile Menu Header -->
    <div class="flex items-center justify-between h-[70px] px-0">
      <a
        [routerLink]="authService.isAuthenticated() ? '/dashboard' : '/'"
        class="flex items-center gap-2"
        (click)="closeMobileMenu()"
      >
        <!-- Light Mode Logo -->
        <img
          src="/assets/svgs/logo.svg"
          class="dark:hidden max-w-[130px]"
          alt="Logo"
        />

        <!-- Dark Mode Logo -->
        <img
          src="/assets/svgs/logo-white.svg"
          class="hidden dark:block max-w-[110px] mt-[-2px]"
          alt="Logo White"
        />
      </a>
      <!-- Close Button -->
      <button
        class="p-2.5 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center justify-center"
        (click)="toggleMobileMenu()"
        aria-label="Close menu"
      >
        <hugeicons-icon
          [icon]="icons.cancel"
          [size]="20"
          color="currentColor"
          [strokeWidth]="2"
          class="block"
        />
      </button>
    </div>

    <!-- Mobile Menu Items -->
    <div class="py-4">
      <!-- Logged In Mobile Menu -->
      <ng-container *ngIf="authService.isAuthenticated()">
        <!-- Navigation Sections -->

        <!-- Main Navigation -->
        <div class="mb-6">
          <h3
            class="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 px-3"
          >
            Menu
          </h3>
          <div class="space-y-0.5">
            <a
              routerLink="/dashboard"
              routerLinkActive="mobile-nav-active"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-indigo-100 rounded-md flex items-center justify-center"
              >
                <hugeicons-icon
                  [icon]="icons.home"
                  [size]="20"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="block text-indigo-700"
                  aria-hidden="true"
                />
              </div>
              <span class="font-medium text-black dark:text-white"
                >Dashboard</span
              >
            </a>

            <a
              routerLink="/jobs"
              routerLinkActive="mobile-nav-active"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-blue-100 rounded-md flex items-center justify-center"
              >
                <hugeicons-icon
                  [icon]="icons.briefcase"
                  [size]="20"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="block text-blue-700"
                  aria-hidden="true"
                />
              </div>
              <span class="font-medium text-black dark:text-white"
                >Browse jobs</span
              >
            </a>

            <a
              routerLink="/job-matches"
              routerLinkActive="mobile-nav-active"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-pink-100 rounded-md flex items-center justify-center"
              >
                <hugeicons-icon
                  [icon]="icons.clipboard"
                  [size]="20"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="block text-pink-700"
                  aria-hidden="true"
                />
              </div>
              <span class="font-medium text-black dark:text-white"
                >Matches</span
              >
              <span
                *ngIf="jobMatchesCount > 0"
                [ngClass]="{
                  'ml-auto bg-brand-gradient text-white text-[10px] font-semibold py-0.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center': true,
                  'px-1.5': jobMatchesCount <= 99,
                  'px-2': jobMatchesCount > 99
                }"
              >
                {{ jobMatchesCount > 99 ? "99+" : jobMatchesCount }}
              </span>
            </a>

            <a
              routerLink="/profile"
              routerLinkActive="mobile-nav-active"
              [routerLinkActiveOptions]="{ exact: false }"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-teal-100 rounded-md flex items-center justify-center"
              >
                <hugeicons-icon
                  [icon]="icons.user"
                  [size]="20"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="block text-teal-700"
                  aria-hidden="true"
                />
              </div>
              <span class="font-medium text-black dark:text-white">Profile</span>
            </a>

            <a
              routerLink="/job-alerts"
              routerLinkActive="mobile-nav-active"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-amber-100 rounded-md flex items-center justify-center"
              >
                <hugeicons-icon
                  [icon]="icons.notification"
                  [size]="20"
                  color="currentColor"
                  [strokeWidth]="2"
                  class="block text-amber-700"
                  aria-hidden="true"
                />
              </div>
              <span class="font-medium text-black dark:text-white"
                >Job alerts</span
              >
            </a>
          </div>
        </div>

        <!-- Inspirational Quote -->
        <div class="mb-6">
          <div
            class="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-4"
          >
            <div class="text-center">
              <p
                class="text-sm font-medium text-gray-900 dark:text-white leading-relaxed mb-2"
              >
                "{{ (dailyQuotesService.currentQuote$ | async)?.text }}"
              </p>
              <p class="text-xs text-gray-600 dark:text-gray-400 font-semibold">
                — {{ (dailyQuotesService.currentQuote$ | async)?.author }}
              </p>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="mb-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div class="space-y-3 px-3">
            <a
              routerLink="/post-job"
              (click)="closeMobileMenu()"
              class="btn-primary font-semibold block text-center"
            >
              Post a job
            </a>

            <button
              (click)="logout(); closeMobileMenu()"
              class="btn-secondary w-full text-center"
            >
              Sign out
            </button>

            <button
              (click)="themeService.toggleTheme()"
              class="flex items-center justify-center w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl text-base text-gray-700 dark:text-gray-300"
            >
              <hugeicons-icon
                [icon]="(themeService.isDarkTheme$ | async) ? icons.sun : icons.moon"
                [size]="20"
                color="currentColor"
                [strokeWidth]="2"
                class="mr-3 block"
              />
              Switch to
              {{ (themeService.isDarkTheme$ | async) ? "light" : "dark" }} mode
            </button>
          </div>
        </div>
      </ng-container>

      <!-- Logged Out Mobile Menu -->
      <ng-container *ngIf="!authService.isAuthenticated()">
        <!-- Find Work Section -->
        <div class="mb-6">
          <h3
            class="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 px-3"
          >
            Find Work
          </h3>
          <div class="space-y-0.5">
            <!-- <a
              routerLink="/for-designers"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-cyan-100 rounded-md flex items-center justify-center"
              >
                <span class="text-lg">👤</span>
              </div>
              <div class="flex-1">
                <span class="font-bold block text-black dark:text-white"
                  >Create Profile</span
                >
                <span class="text-sm text-gray-600 dark:text-gray-400"
                  >Showcase your work</span
                >
              </div>
            </a> -->

            <a
              routerLink="/jobs"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-blue-100 rounded-md flex items-center justify-center"
              >
                <span class="text-lg">💼</span>
              </div>
              <div class="flex-1">
                <span class="font-bold block text-black dark:text-white"
                  >Browse Jobs</span
                >
                <span class="text-sm text-gray-600 dark:text-gray-400"
                  >Find your next design role</span
                >
              </div>
            </a>

            <a
              routerLink="/ai-job-matcher"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-green-100 rounded-md flex items-center justify-center"
              >
                <span class="text-lg">💡</span>
              </div>
              <div class="flex-1">
                <span class="font-bold block text-black dark:text-white"
                  >AI Job Matcher</span
                >
                <span class="text-sm text-gray-600 dark:text-gray-400"
                  >Let AI find your perfect job</span
                >
              </div>
            </a>

            <a
              routerLink="/companies-hiring-this-week"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-pink-100 rounded-md flex items-center justify-center"
              >
                <span class="text-lg">🏢</span>
              </div>
              <div class="flex-1">
                <span class="font-bold block text-black dark:text-white"
                  >Companies hiring</span
                >
                <span class="text-sm text-gray-600 dark:text-gray-400"
                  >See who's hiring this week</span
                >
              </div>
            </a>
          </div>
        </div>

        <!-- For Employers Section -->
        <div class="mb-6">
          <h3
            class="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 px-3"
          >
            For Employers
          </h3>
          <div class="space-y-0.5">
            <a
              routerLink="/hire-designers"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-purple-100 rounded-md flex items-center justify-center"
              >
                <span class="text-lg">📊</span>
              </div>
              <div class="flex-1">
                <span class="font-bold block text-black dark:text-white"
                  >Hire designers</span
                >
                <span class="text-sm text-gray-600 dark:text-gray-400"
                  >Why post &amp; how it works</span
                >
              </div>
            </a>
          </div>
        </div>

        <!-- Resources Section -->
        <div class="mb-6">
          <h3
            class="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1 px-3"
          >
            Resources
          </h3>
          <div class="space-y-0.5">
            <a
              routerLink="/blog"
              (click)="closeMobileMenu()"
              class="flex items-center gap-3 px-3 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div
                class="min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex-shrink-0 bg-yellow-100 rounded-md flex items-center justify-center"
              >
                <span class="text-lg">📖</span>
              </div>
              <div class="flex-1">
                <span class="font-bold block text-black dark:text-white"
                  >Blog & Guides</span
                >
                <span class="text-sm text-gray-600 dark:text-gray-400"
                  >Tools and guides for your design career</span
                >
              </div>
            </a>
          </div>
        </div>

        <!-- Inspirational Quote -->
        <div class="mb-6">
          <div
            class="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-4"
          >
            <div class="text-center">
              <p
                class="text-sm font-medium text-gray-900 dark:text-white leading-relaxed mb-2"
              >
                "{{ (dailyQuotesService.currentQuote$ | async)?.text }}"
              </p>
              <p class="text-xs text-gray-600 dark:text-gray-400 font-semibold">
                — {{ (dailyQuotesService.currentQuote$ | async)?.author }}
              </p>
            </div>
          </div>
        </div>

        <!-- Authentication Section -->
        <div class="px-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div class="space-y-3">
            <a
              href="/"
              (click)="login($event); closeMobileMenu()"
              class="w-full btn-secondary text-center cursor-pointer"
            >
              Login
            </a>

            <a
              href="/"
              (click)="register($event); closeMobileMenu()"
              class="w-full btn-primary font-semibold text-center cursor-pointer"
            >
              Sign up
            </a>

            <button
              (click)="themeService.toggleTheme()"
              class="flex items-center justify-center w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl text-base text-gray-700 dark:text-gray-300"
            >
              <hugeicons-icon
                [icon]="(themeService.isDarkTheme$ | async) ? icons.sun : icons.moon"
                [size]="20"
                color="currentColor"
                [strokeWidth]="2"
                class="mr-3 block"
              />
              Switch to
              {{ (themeService.isDarkTheme$ | async) ? "light" : "dark" }} mode
            </button>
          </div>
        </div>
      </ng-container>
    </div>
  </div>
</div>

<div class="homepage-gradient -z-10"></div>